import {
    ActionID,
    AppIdentifiers,
    ChatOpenMode,
    ICrmChannel,
    ILogger,
    INavigationChannel,
    IPaymentActionData,
    IUiChannel,
    MessageBroker,
    PaymentAppType,
    PostLoginActionNameEnum,
    ScrollGames,
    TriggerType,
} from '@sparkware/uc-sdk-core';
import { IPostMessageHandler, IPostMessageService } from '../post-message';
import Container from 'typedi';
import { LoggerProvider } from '../logger';
import { IActionMapperResponse } from './models/IActionMapperResponse';
import { ViewInjector } from '../view-injector';
import { CtaHandlerActionIds, IdsOfElements } from '../../models/enums/general-enums';
import DialogType from '../ui/enums/DialogType';
import { UIService } from '../ui';
import { LoaderManager } from '../../loaders/LoaderManager';
import { IPlayer } from '../player/models';
import { ICrossSell } from '../cross-sell/models/ICrossSell';
import { ICRM } from '../crm/models';
import { Utils } from '../utils';
import { UserContextToken, WindowToken } from '../../injection-tokens';
import { OMGUIManager } from '../view-injector/utils/OMGUIManager';
import { PostLoginStorage } from '../post-login/post-login.storage';
import { IPostLogin } from '../post-login/models/IPostLogin';
import { FlowsRunner } from '../flows-runner';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { v4 as uuidv4 } from 'uuid';
import { Payments } from '../payments';
import { IUserContext } from '../user-context/user-context-interface';

export class CtaHandler implements IPostMessageHandler {
    private readonly _logger: ILogger;
    protected readonly _viewInjector: ViewInjector;
    private readonly _navigationChannel: INavigationChannel;
    protected readonly _uiService: UIService;
    private readonly _utils: Utils;
    private readonly _window: Window;
    private readonly _omgUIManager: OMGUIManager;
    private readonly _postLoginStorage: PostLoginStorage;
    private readonly _flowsRunner: FlowsRunner;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _uiChannel: IUiChannel;
    private readonly _crmChannel: ICrmChannel;
    private readonly _payments: Payments;
    private readonly _userContext: IUserContext;

    private get _playerPromise(): Promise<IPlayer> {
        return LoaderManager.Instance.PlayerLoader.Instance;
    }

    private get _crossSellPromise(): Promise<ICrossSell> {
        return LoaderManager.Instance.CrossSellLoader.Instance;
    }

    private get _crmPromise(): Promise<ICRM> {
        return LoaderManager.Instance.CRMLoader.Instance;
    }

    private get _postLoginPromise(): Promise<IPostLogin> {
        return LoaderManager.Instance.PostLoginLoader.Instance;
    }

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }

    private get _postMessageServicePromise(): Promise<IPostMessageService> {
        return LoaderManager.Instance.PostMessageServiceLoader.Instance;
    }

    public get name(): string {
        return 'CTAPostMessageHandler';
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger(this.name);
        this._flowsRunner = Container.get(FlowsRunner);
        this._viewInjector = Container.get(ViewInjector);
        this._uiService = Container.get(UIService);
        this._utils = Container.get(Utils);
        this._window = Container.get(WindowToken);
        this._omgUIManager = Container.get(OMGUIManager);
        this._postLoginStorage = Container.get(PostLoginStorage);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._payments = Container.get(Payments);

        this._navigationChannel = MessageBroker.getInstance().navigation;
        this._uiChannel = MessageBroker.getInstance().ui;
        this._crmChannel = MessageBroker.getInstance().crm;
        this._userContext = Container.get(UserContextToken);

        this._registerPostMessageHandler();
    }

    public handler = (event: any): Promise<boolean> => this._handleMessages(event);

    private _registerPostMessageHandler = async (): Promise<void> => {
        this._logger.debug(`Registering ${this.name}...`);
        const postMessageService = await this._postMessageServicePromise;
        postMessageService?.addHandler(this);
    };

    private _handleMessages = async (event: MessageEvent): Promise<boolean> => {
        this._logger.debug('CTA handler called', event.data);
        const { data } = event;
        if (typeof data !== 'object' || !data?.ActionId) {
            return false;
        }

        const response = this._getActionResponse(data);
        if (response.closeWrapDiv) {
            await this._viewInjector.CloseWrapDiv();
        }

        if (typeof response.actionFn === 'function') {
            await response.actionFn();
        }

        //TODO: We return false to simulate the previous parallel execution - After refactoring the postMessageHandler, we should remove it.
        return false;
    };

    private _getChannelData(messageData: any): IPaymentActionData {
        if (!messageData) return {};
        if (messageData.Channel) return messageData.Channel;

        let source = messageData?.Source;
        let element = messageData?.Element;
        let area = messageData?.Area;

        if (messageData.originInformation) {
            if (messageData.originInformation.originProductPackageID == 27) {
                source = 'Pending action';
                area = 'Pending action';
                element = messageData.Name || 'UD';
            } else if (messageData.originInformation.originProductPackageID == 10) {
                source = 'My account';
                area = 'Header';
                element = messageData.tabId === '4' ? 'Withdraw button' : 'Deposit button';
            }
        }

        return {
            Source: source,
            Element: element,
            Area: area,
        };
    }

    private _getActionResponse(messageData: any): IActionMapperResponse {
        const response: IActionMapperResponse = {
            closeWrapDiv: true,
        };

        switch (messageData.ActionId.toLowerCase()) {
            case CtaHandlerActionIds.Cashier:
                response.actionFn = async () => {
                    const channelData = this._getChannelData(messageData);
                    void this._openCashier(messageData.TabId, messageData.PromCode, channelData);
                };
                break;

            case CtaHandlerActionIds.QuickDeposit:
                response.actionFn = async () => {
                    const tabId = messageData?.TabId || '16';
                    const promoCode = messageData?.PromoCode || messageData?.PromCode || null;
                    const channelData = this._getChannelData(messageData);
                    const appType = messageData?.AppType;
                    void this._openCashier(tabId, promoCode, channelData, appType);
                };
                break;

            case CtaHandlerActionIds.RingGame:
                response.actionFn = async () => {
                    this._navigationChannel.topics.scrollToGame.publish(
                        {
                            publisher: this.name,
                        },
                        { gameName: ScrollGames.ring },
                    );
                };
                break;

            case CtaHandlerActionIds.TournamentLobby:
            case CtaHandlerActionIds.JoinTournament:
            case CtaHandlerActionIds.OnlineShop:
            case CtaHandlerActionIds.PlayWithFriends:
            case CtaHandlerActionIds.ScheduledTournaments:
                response.actionFn = async () => {
                    this._uiService.handleDialog({ dialogType: DialogType.PokerDownload });
                };
                break;

            case CtaHandlerActionIds.MyAccount:
                response.actionFn = async () => {
                    const isAuthenticated = this._userContext.IsAuthenticated;
                    if (isAuthenticated)
                        (await this._playerPromise)?.onMyAccountOpen({ tabId: messageData.TabId });
                };
                break;

            case CtaHandlerActionIds.Casino:
                response.actionFn = async () => {
                    (await this._crossSellPromise)?.onCasinoClientOpen({
                        target: messageData.target,
                        querystring: messageData.queryString,
                    });
                };
                break;

            case CtaHandlerActionIds.TournamentTickets:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({ tabId: 10 });
                };
                break;

            case CtaHandlerActionIds.ExternalUrl:
                response.actionFn = async () => {
                    if (this._utils.findIfIsNative()) {
                        (await this._crmPromise)?.onExternalLinkOpen({ Url: messageData.Url });
                    }
                };
                break;

            case CtaHandlerActionIds.MyRewards:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({ tabId: 3 });
                };
                break;

            case CtaHandlerActionIds.InternalUrl:
                response.actionFn = async () => {
                    this._window.location.href = messageData.Url;
                };
                break;

            case CtaHandlerActionIds.Snap:
                response.actionFn = async () => {
                    this._navigationChannel.topics.scrollToGame.publish(
                        {
                            publisher: this.name,
                        },
                        { gameName: ScrollGames.snap },
                    );
                };
                break;

            case CtaHandlerActionIds.RedeemCompPoints:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({ tabId: 8 });
                };
                break;

            case CtaHandlerActionIds.ShowMe:
                response.closeWrapDiv = false;
                response.actionFn = async () => {
                    this._updateOMGContainer(messageData.Width, messageData.Height);
                };
                break;

            case CtaHandlerActionIds.CloseMe:
                response.actionFn = async () => {
                    const actionsToComplete = [
                        PostLoginActionNameEnum.UploadDocuments,
                        PostLoginActionNameEnum.OMGGetWaitingMessages,
                        PostLoginActionNameEnum.ShowKYC,
                        PostLoginActionNameEnum.CheckForPendingIdentityVerification,
                        PostLoginActionNameEnum.OpenCashier,
                    ];
                    const currentAction = this._postLoginStorage.getCurrentPostLoginAction();
                    if (
                        currentAction &&
                        actionsToComplete.find((actionName) => actionName === currentAction.Name)
                    ) {
                        (await this._postLoginPromise)?.completeAction(currentAction.Name);
                    }
                    this._omgUIManager.omgShown = false;
                };
                break;

            case CtaHandlerActionIds.PokerCampaigns:
                response.actionFn = async () => {
                    const { isMobile } = this._window.pageContextManager.getDeviceData();
                    if (isMobile) {
                        this._viewInjector.OpenMobileContainerDiv(messageData.url, true);
                    } else {
                        this._viewInjector.InjectWrappingDivWithCloseButton(
                            messageData.url,
                            940,
                            658,
                            true,
                        );
                    }
                };
                break;

            case CtaHandlerActionIds.RichCampaigns:
                response.actionFn = async () => {
                    if (!messageData.campaignID) {
                        this._logger.log('No Rich CampaignId received. cannot open rich popup');
                        return;
                    }
                    (await this._crmPromise)?.onRichOpen({ CampaignId: messageData.campaignID });
                };
                break;

            case CtaHandlerActionIds.Challenges:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({ tabId: 16 });
                };
                break;

            case CtaHandlerActionIds.OpenLoyalty:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({ tabId: 7 });
                };
                break;

            case CtaHandlerActionIds.Ready:
                response.closeWrapDiv = false;
                if (messageData.Name === 'Ready' && messageData.IsSuccess) {
                    response.actionFn = async () => {};
                }
                const actionStarted = this._localSimpleStoreService.get(
                    StorageItemEnum.ActionStarted,
                );
                if (actionStarted === ActionID.openChallenges) {
                    void this._flowsRunner.onActionCompleted();
                }
                break;

            case CtaHandlerActionIds.PokerBlast:
                response.actionFn = async () => {
                    const channelData = {
                        area: messageData.Channel?.Area || messageData.Channel?.area,
                        element: messageData.Channel?.Element || messageData.Channel?.element,
                        source: messageData.Channel?.Source || messageData.Channel?.source,
                    };
                    (await this._actionHandlerFactoryPromise)?.performAction({
                        actionID: ActionID.openPokerGame,
                        launchInfo: {
                            businessCorrelationID: uuidv4(),
                            sequentialCorrelationID: 1,
                            channel: channelData,
                            appSpecificParameters: {
                                blastPoolId: parseInt(messageData.blastPoolId),
                            },
                            trigger: TriggerType.userSelection,
                            containerID: undefined,
                            sourceAppID: AppIdentifiers.UnifiedClient,
                            sourceAppVersion: '0.0.0',
                            clientVersion: '0',
                        },
                        actionData: {},
                    });
                };
                break;

            case CtaHandlerActionIds.FavoriteSports:
                response.actionFn = async () => {
                    const publisher = messageData?.Channel?.Source || 'unknown';
                    this._uiChannel.topics.openFavouriteSportsModal.publish(
                        {
                            publisher: `CF-CTA_HANDLER-${publisher}`,
                        },
                        {},
                    );
                };
                break;

            case CtaHandlerActionIds.VerificationModule:
                response.actionFn = async () => {
                    (await this._playerPromise)?.onMyAccountOpen({
                        tabId: 16,
                        hideHeader: true,
                        hideMenu: true,
                    });
                };
                break;

            case CtaHandlerActionIds.TournamentTicketsOpened:
                response.closeWrapDiv = false;
                response.actionFn = async () => {
                    this._crmChannel.topics.TOURNAMENT_TICKETS_TAB_OPENED.publish(
                        { publisher: this.name },
                        {},
                    );
                };
                break;

            case CtaHandlerActionIds.Pending.toLowerCase():
                response.actionFn = async () => {
                    const currentAction = this._postLoginStorage.getCurrentPostLoginAction();
                    if (
                        currentAction &&
                        currentAction.Name ===
                            PostLoginActionNameEnum.CheckForPendingIdentityVerification
                    ) {
                        (await this._postLoginPromise)?.completeAction(currentAction.Name);
                    }
                };
                break;

            case CtaHandlerActionIds.OnReady:
                response.closeWrapDiv = false;
                break;

            case CtaHandlerActionIds.OpenChat:
                response.actionFn = async () => {
                    const channelData = {
                        area: messageData.Channel?.Area || messageData.Channel?.area,
                        element: messageData.Channel?.Element || messageData.Channel?.element,
                        source: messageData.Channel?.Source || messageData.Channel?.source,
                    };
                    (await this._actionHandlerFactoryPromise)?.performAction({
                        actionID: ActionID.openChat,
                        launchInfo: {
                            businessCorrelationID: uuidv4(),
                            sequentialCorrelationID: 1,
                            channel: channelData,
                            appSpecificParameters: {
                                openMode: ChatOpenMode.PendingDocuments,
                            },
                            trigger: TriggerType.userSelection,
                            containerID: undefined,
                            sourceAppID: AppIdentifiers.UnifiedClient,
                            sourceAppVersion: '0.0.0',
                            clientVersion: '0',
                        },
                        actionData: {},
                    });
                };
                break;

            default:
                this._logger.debug(`Action isn't supported. actionId: ${messageData.ActionId}`);
        }

        return response;
    }
    private _updateOMGContainer(width: number, height: number): void {
        const overlay = this._window.document.getElementById('overlay');
        const omgGeneralIframe = this._window.document.getElementById(
            IdsOfElements.OmgGeneralIframe,
        );

        if (overlay && omgGeneralIframe) {
            overlay.style.display = 'block';
        }

        // Update container size through UI service
        this._omgUIManager.updateContainerSize(width, height);
        this._omgUIManager.omgShown = true;

        // Handle mobile specific logic
        const { isMobile } = this._window.pageContextManager.getDeviceData();
        if (isMobile) {
            const generalIframe = this._window.document.getElementById('generalIframe');
            if (generalIframe) {
                generalIframe.classList.remove('noBorder');
            }
        }
    }

    private async _openCashier(
        tabId: string,
        promoCode: string | null,
        channel: IPaymentActionData,
        appType?: PaymentAppType,
    ): Promise<void> {
        const actionId = tabId === '4' ? 'withdraw' : 'deposit';
        const tabIdNumber = Number(tabId);
        const selectedAppType = appType || this._payments.getPaymentAppType(tabIdNumber);
        await this._payments.onPaymentOpen({
            PromoCode: promoCode || '',
            TabId: tabIdNumber || 1,
            ActionId: actionId,
            AppType: selectedAppType,
            ...channel,
        });
    }
}
